import { AlertCircle, TrendingDown, TrendingUp, Users } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

type Props = {
  totalRecipients: number;
  completedCount: number;
  pendingCount: number;
  failedCount: number;
  completedPercentage: number;
  pendingPercentage: number;
  failedPercentage: number;
};

export function BatchCallKpiCards({
  totalRecipients,
  completedCount,
  pendingCount,
  failedCount,
  completedPercentage,
  pendingPercentage,
  failedPercentage,
}: Props) {
  return (
    <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs sm:grid-cols-2 lg:grid-cols-4">
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>Total Recipients</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {totalRecipients}
          </CardTitle>
          <CardAction>
            <Badge variant="outline">
              <Users className="mr-1 h-3 w-3" />
              100%
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <div className="line-clamp-1 flex gap-2 font-medium">Total contacts</div>
          <div className="text-muted-foreground">Recipients in this batch</div>
        </CardFooter>
      </Card>

      <Card className="@container/card">
        <CardHeader>
          <CardDescription>Completed</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {completedCount}
          </CardTitle>
          <CardAction>
            <Badge variant="success">
              <TrendingUp className="mr-1 h-3 w-3" />
              {completedPercentage}%
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <div className="line-clamp-1 flex gap-2 font-medium">
            Trending up <TrendingUp className="h-4 w-4" />
          </div>
          <div className="text-muted-foreground">Calls answered successfully</div>
        </CardFooter>
      </Card>

      <Card className="@container/card">
        <CardHeader>
          <CardDescription>Pending</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {pendingCount}
          </CardTitle>
          <CardAction>
            <Badge variant="outline">
              <AlertCircle className="mr-1 h-3 w-3" />
              {pendingPercentage}%
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <div className="line-clamp-1 flex gap-2 font-medium">Awaiting completion</div>
          <div className="text-muted-foreground">Calls not yet attempted</div>
        </CardFooter>
      </Card>

      <Card className="@container/card">
        <CardHeader>
          <CardDescription>Failed</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {failedCount}
          </CardTitle>
          <CardAction>
            <Badge variant="fail">
              <TrendingDown className="mr-1 h-3 w-3" />
              {failedPercentage}%
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <div className="line-clamp-1 flex gap-2 font-medium">
            Needs attention <TrendingDown className="h-4 w-4" />
          </div>
          <div className="text-muted-foreground">Failed or unreachable calls</div>
        </CardFooter>
      </Card>
    </div>
  );
}
